import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { OrdersService } from '../../orders.service';
import { finalize, Subscription } from 'rxjs';
import { PaginatedResponse } from '../../../common/models/common.model';
import { emptyOrderEntityFilters, OrderEntityFilters, Shipment, ShipmentItem } from '../../orders.models';
import { SnackbarService } from '../../../services/snackbar.service';
import { buildFilterQuery, SnackbarAlert, SnackbarAlertStatus } from '../../../utils';

@Component({
  selector: 'app-shipments',
  standalone: false,
  templateUrl: './shipments.component.html',
  styleUrl: './shipments.component.scss',
})
export class ShipmentsComponent implements OnInit, OnDestroy {
  protected shipments: Shipment[] = [];
  protected isDataLoading = false;
  protected isExporting = false;
  protected hasNextPage: boolean = false;
  protected hasPreviousPage: boolean = false;
  protected pageSize: number = 10;
  protected totalCount: number = 0;
  protected currentPage: number = 1;

  protected readonly statusOptions = [
    { label: 'Dispatched', value: 'dispatched' },
    { label: 'Delivered', value: 'delivered' },
    { label: 'In Transit', value: 'in_transit' },
  ];
  protected selectedStatus: string = 'dispatched';
  protected filters: OrderEntityFilters = emptyOrderEntityFilters();

  private ordersSubscription!: Subscription;

  constructor(
    private ordersService: OrdersService,
    private snackbarService: SnackbarService,
    private router: Router
  ) {}

  ngOnInit() {
    this.loadShipments();
  }

  protected get hasActiveFilters(): boolean {
    return Object.values(this.filters).some(Boolean);
  }

  protected onFiltersChange(filters: OrderEntityFilters): void {
    this.filters = filters;
    // A newly filtered result set may not have the page the user is currently on.
    this.currentPage = 1;
    this.loadShipments();
  }

  /** Empty filters are dropped so the query string never carries `product=` or `product=null`. */
  private get activeFilterParams(): Record<string, string> {
    return buildFilterQuery(this.filters);
  }

  private loadShipments() {
    this.isDataLoading = true;
    const params: any = { page: this.currentPage, ...this.activeFilterParams };
    if (this.selectedStatus) {
      params['status'] = this.selectedStatus;
    }

    this.ordersSubscription = this.ordersService
      .get_shipments(params)
      .pipe(finalize(() => (this.isDataLoading = false)))
      .subscribe({
        next: (data: PaginatedResponse<Shipment>) => {
          this.shipments = data.results;
          this.hasNextPage = !!data.next;
          this.hasPreviousPage = !!data.previous;
          this.totalCount = data.count || 0;
        },
      });
  }

  protected getProductName(item: ShipmentItem): string {
    const product = item.order_item?.product_packaging?.product;

    return typeof product === 'string' ? product : ((product as any)?.name ?? '—');
  }

  /** The shipments endpoint has no total field, so the dispatched total is summed from its items. */
  protected getTotalBoxes(shipment: Shipment): number {
    return (shipment.items ?? []).reduce((total, item) => total + (item.box_quantity ?? 0), 0);
  }

  protected onStatusChange(): void {
    this.currentPage = 1;
    this.loadShipments();
  }

  protected viewShipment(shipmentId: string): void {
    void this.router.navigate(['/orders/shipments', shipmentId]);
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this.loadShipments();
  }

  protected exportDispatched(): void {
    this.isExporting = true;

    // Same filters as the list — including status — so the file matches the screen.
    const params: Record<string, string> = { ...this.activeFilterParams };
    if (this.selectedStatus) {
      params['status'] = this.selectedStatus;
    }

    this.ordersService
      .exportDispatched(params)
      .pipe(finalize(() => (this.isExporting = false)))
      .subscribe({
        next: response => {
          const blob = response.body!;
          const disposition = response.headers.get('Content-Disposition') ?? '';
          const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          const filename =
            match?.[1]?.replace(/['"]/g, '') ||
            `Dispatched_Orders_Report_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.xlsx`;

          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
        },
        error: () => {
          this.snackbarService.show(SnackbarAlert.SOMETHING_WENT_WRONG, SnackbarAlertStatus.ERROR);
        },
      });
  }

  ngOnDestroy() {
    this.ordersSubscription?.unsubscribe();
  }
}
